import React from 'react';
import PropTypes from 'prop-types';
import { getPageOnHostByID } from 'lib/api';
import { requireQueryByName } from 'lib/dependencies';
import Media from 'react-media';

import GLOBAL_MEDIA_QUERIES from 'utils/facility';
import { formatAvailableDaysFromSchedules } from 'helpers/schedule';

import Breadcrumbs from 'components/Core/Breadcrumbs';
import Hero from 'components/Core/Hero';
import IconSvg from 'components/Core/IconSvg';
import RichText from 'components/Core/RichText';
import EmbedBlock from 'components/Core/Embed';
import ActivityDesc from 'components/ActivityDesc';
import ActivityChecklist from 'components/ActivityChecklist';
import SocialShareBar from 'components/Core/SocialShareBar';
import OpeningTimes from 'components/OpeningTimes';
import ActivityTickets from 'components/ActivityTickets';

import { PageWrap, TitleWrap, ContentWrap, LabelWrap } from './CustomActivityPage.styles';

const CustomActivityPage = (props) => {
  const {
    page: {
      title: activityTitle,
      heroFallbackImage: heroImage,
      descriptionTitle: activityDescTitle,
      descriptionRt: activityDesc,
      embedHtml,
      descriptionLower,
      heroVideo,
      areaChoiceDisplay: areaChoice,
      checklistTitle,
      checklistImage,
      checklist,
      theme: { icon: themeIcon },
      breadcrumbs,
      schedule,
      price,
      socialShareTitle,
    },
    unusualopeninghours: { hideOpeningHours },
  } = props;
  return (
    <>
      <div className="absolute z-10 w-full">
        <div className="container mx-auto px-4">
          <Breadcrumbs light breadcrumbs={breadcrumbs} />
        </div>
      </div>
      {heroImage && <Hero hero={heroImage} video={heroVideo} />}
      <PageWrap>
        <TitleWrap>
          <div className="w-full mb-4">
            <Media queries={GLOBAL_MEDIA_QUERIES} defaultMatches={{ sm: true }}>
              {(matches) =>
                matches.sm ? (
                  <>
                    <h5 className="uppercase font-normal text-xs">{areaChoice}</h5>
                    <h3 className="font-black tracking-tight">{activityTitle}</h3>
                  </>
                ) : (
                  <>
                    <h2 className="uppercase font-normal">{areaChoice}</h2>
                    <h1 className="font-black tracking-tight">{activityTitle}</h1>
                  </>
                )
              }
            </Media>
          </div>
          <div className="w-full flex md:my-4">
            <LabelWrap themeIcon={themeIcon}>
              <Media queries={GLOBAL_MEDIA_QUERIES} defaultMatches={{ sm: true }}>
                {(matches) =>
                  matches.sm ? (
                    <IconSvg icon={themeIcon} size={22} className="mx-auto" />
                  ) : (
                    <IconSvg icon={themeIcon} size={32} className="mx-auto" />
                  )
                }
              </Media>
            </LabelWrap>
          </div>
        </TitleWrap>
        <ContentWrap>
          <div className="container px-4 mx-auto">
            <div className="flex flex-wrap justify-between -mx-4">
              <div className="flex-full max-w-full lg:flex-1 xl:flex-1/2 xl:max-w-1/2 px-4">
                <ActivityDesc activityDescTitle={activityDescTitle} activityDesc={activityDesc} />
                {embedHtml && (
                  <div className="my-6 lg:mt-12 lg:mb-10" data-test-id="component-activity-embed">
                    <EmbedBlock embed={embedHtml} />
                  </div>
                )}
                {descriptionLower && <RichText blockMargin={false} content={descriptionLower} />}
              </div>
              <aside className="flex-full max-w-full lg:flex-1/3 lg:max-w-1/3 mt-6 lg:mt-12 px-4">
                <SocialShareBar articleTitle={activityTitle} socialShareTitle={socialShareTitle} />
                {schedule && (
                  <div className="my-6 lg:my-12">
                    <OpeningTimes
                      title="Tijden"
                      activityTitle={activityTitle}
                      availableDays={formatAvailableDaysFromSchedules(schedule)}
                      hideOpeningHours={hideOpeningHours}
                      {...schedule}
                    />
                  </div>
                )}
              </aside>
            </div>
          </div>
          <ActivityChecklist
            checkListTitle={checklistTitle}
            checkListItems={checklist.reduce((acc, item) => [...acc, item.content], [])}
            checklistImage={checklistImage}
          />
          {price && (
            <div className="container px-4 mx-auto u-block-margins">
              <h3 className="mb-6 lg:mb-10">Tarieven</h3>
              <ActivityTickets tickets={price.tickets} />
            </div>
          )}
        </ContentWrap>
      </PageWrap>
    </>
  );
};

CustomActivityPage.getCustomInitialProps = async ({ id, contentType, hostname, previewData, unusualopeninghours }) => {
  const query = requireQueryByName('CustomActivityPage');
  const token = previewData ? previewData.token : '';
  const { page } = await getPageOnHostByID({
    id,
    contentType,
    query,
    token,
    hostname,
  });

  return {
    page,
    extraPageProps: {
      unusualopeninghours,
    },
  };
};

CustomActivityPage.defaultProps = {
  page: {
    checklist: [],
    embedHtml: '',
    descriptionLower: '',
    schedule: null,
    price: null,
  },
  unusualopeninghours: {
    hideOpeningHours: false,
  },
};

CustomActivityPage.propTypes = {
  page: PropTypes.shape({
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    checklist: PropTypes.arrayOf(
      PropTypes.shape({
        content: PropTypes.string.isRequired,
      }),
    ),
    descriptionTitle: PropTypes.string.isRequired,
    descriptionRt: PropTypes.string.isRequired,
    embedHtml: PropTypes.string,
    descriptionLower: PropTypes.string,
    areaChoiceDisplay: PropTypes.string.isRequired,
    checklistTitle: PropTypes.string.isRequired,
    checklistImage: PropTypes.shape({
      title: PropTypes.string,
      xl: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
    }).isRequired,
    theme: PropTypes.shape({
      icon: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    heroVideo: PropTypes.shape({
      url: PropTypes.string.isRequired,
      title: PropTypes.string,
    }),
    heroFallbackImage: PropTypes.shape({
      xl: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
      md: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
      sm: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
      xs: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
    }).isRequired,
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    ).isRequired,
    schedule: PropTypes.shape({
      availableDays: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    price: PropTypes.shape({
      tickets: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          price: PropTypes.string.isRequired,
          url: PropTypes.string,
        }),
      ),
    }),
    socialShareTitle: PropTypes.string,
  }),
  unusualopeninghours: PropTypes.shape({
    hideOpeningHours: PropTypes.bool,
  }),
};

export default CustomActivityPage;
